// client/src/components/map/assetStatus.ts
//
// THE asset status colour scheme (ST-00253 / ST-00244, 2026-10-19): one key per thing a truck
// or loader can be doing, one set of colours per key, used by every LexiStar surface — the
// Status Board (control room cards and chips, table and Kanban pills, tiles), Live View (map
// markers, the vehicle rail, the detail panel, the legend), Replay (track by stage, the Stage
// lane, the legend, the marker), the Trips playback marker and the asset marker itself.
//
// One truck → one key (so the same truck has the same colour on every screen at the same
// moment):
//   - a truck with a LexiStar status: the control room's placement of its status board row
//     (StatusBoard/controlRoom.ts placeRow → statusKeyOfRow). The operational context decides;
//     the active trip's stage stands in when the engine has not derived a context yet;
//     maintenance holds while the tracker is silent; stopped / parked in transit is "delayed";
//     no data for 90 minutes is "offline".
//   - a trip stage (Replay): statusKeyOfStage — the key the control room gives a truck in that
//     stage (dispatched / acknowledged = to source; completed = parked).
//   - a two-axis status without the row around it: statusKeyOfStatus, the same precedence.
//   - an asset with no LexiStar status (a tracker that is not in the register, Trips): motion
//     only (statusKeyOfMotion) — moving = unallocated ("moving without a trip"), standing =
//     parked, no recent data = offline.
// Colour is never the only cue: the asset marker's shape (pointed = moving, round = standing,
// dashed = no recent position) and every label carry the meaning too.
//
// Colours. `marker` is the map colour: markers, tracks and the inline marker icons, the same in
// both themes (the basemaps do not follow the theme; the marker's white keyline carries it on
// any background). `light` / `dark` are the app-surface tokens per theme — `solid` (badges,
// swatches, bars, the Replay Stage lane), `on` (an icon on solid), `text` (accent text, ≥ 4.5:1
// on its tint and on the surface), `tint` (a card's panel), `border`. They become CSS custom
// properties (--as-<key>-<part>, installed once from this module) under :root /
// [data-theme='light'] and [data-theme='dark'], so they flip with the theme, also on a wall
// display that sets its own data-theme. ASSET_STATUS_CLASSES are the Tailwind classes over
// those properties (literal strings, so the JIT sees them).

import {
  ASSET_STATUS,
  ASSET_STATUS_ORDER,
  assetStateOfMotion,
  assetStateOfStage,
  assetStateOfStatus,
  resolveAssetKey,
  type AssetStatusKey,
  type AssetStatusSurface,
  type MotionStatus,
  type TwoAxisStatusLike,
} from './assetVisualRules';

// The palette and the rules live in the pure module (map/assetVisualRules); re-exported here.
export {
  ASSET_STATUS,
  ASSET_STATUS_ORDER,
  isAssetStatusKey,
  type AssetStatusKey,
  type AssetStatusSurface,
  type AssetStatusTokens,
  type MotionStatus,
  type TwoAxisStatusLike,
} from './assetVisualRules';

/** The map / marker colour of a key. */
export const assetStatusMarker = (key: AssetStatusKey): string => ASSET_STATUS[key].marker;

// ── CSS custom properties ────────────────────────────────────────────────────────────────────
const slug = (key: AssetStatusKey) => key.replace(/_/g, '-');
const PARTS: (keyof AssetStatusSurface)[] = ['solid', 'on', 'text', 'tint', 'border'];

/** `var(--as-<key>-<part>)` — the theme-aware value of a token. */
export const assetStatusVar = (key: AssetStatusKey, part: keyof AssetStatusSurface): string =>
  `var(--as-${slug(key)}-${part})`;

/** The stylesheet that defines every token for both themes. */
export function assetStatusCss(): string {
  const block = (theme: 'light' | 'dark') =>
    ASSET_STATUS_ORDER.map((k) =>
      PARTS.map((p) => `--as-${slug(k)}-${p}:${ASSET_STATUS[k][theme][p]};`).join('')
    ).join('');
  return `:root,[data-theme='light']{${block('light')}}[data-theme='dark']{${block('dark')}}`;
}

const CSS_ID = 'asset-status-tokens';
/** Install the token stylesheet once (idempotent; a no-op without a document). */
export function installAssetStatusCss(doc: Document | undefined = globalThis.document): void {
  if (!doc || doc.getElementById(CSS_ID)) return;
  const style = doc.createElement('style');
  style.id = CSS_ID;
  style.textContent = assetStatusCss();
  doc.head.appendChild(style);
}
installAssetStatusCss();

// ── Tailwind classes over the tokens (literal, for the JIT) ──────────────────────────────────
export interface AssetStatusClasses {
  /** A card's panel: tint + border. */
  panel: string;
  /** A large count on the panel. */
  count: string;
  /** A solid icon badge. */
  badge: string;
  /** A thin progress bar. */
  bar: string;
  /** Accent text (time in state). */
  text: string;
  /** A status pill: tint, text, ring. */
  pill: string;
  /** A small solid dot / swatch. */
  dot: string;
}

export const ASSET_STATUS_CLASSES: Record<AssetStatusKey, AssetStatusClasses> = {
  to_source: {
    panel: 'bg-[color:var(--as-to-source-tint)] border-[color:var(--as-to-source-border)]',
    count: 'text-[color:var(--as-to-source-text)]',
    badge: 'bg-[color:var(--as-to-source-solid)] text-[color:var(--as-to-source-on)]',
    bar: 'bg-[color:var(--as-to-source-solid)]',
    text: 'text-[color:var(--as-to-source-text)]',
    pill: 'bg-[color:var(--as-to-source-tint)] text-[color:var(--as-to-source-text)] ring-1 ring-inset ring-[color:var(--as-to-source-border)]',
    dot: 'bg-[color:var(--as-to-source-solid)]',
  },
  at_source: {
    panel: 'bg-[color:var(--as-at-source-tint)] border-[color:var(--as-at-source-border)]',
    count: 'text-[color:var(--as-at-source-text)]',
    badge: 'bg-[color:var(--as-at-source-solid)] text-[color:var(--as-at-source-on)]',
    bar: 'bg-[color:var(--as-at-source-solid)]',
    text: 'text-[color:var(--as-at-source-text)]',
    pill: 'bg-[color:var(--as-at-source-tint)] text-[color:var(--as-at-source-text)] ring-1 ring-inset ring-[color:var(--as-at-source-border)]',
    dot: 'bg-[color:var(--as-at-source-solid)]',
  },
  loaded: {
    panel: 'bg-[color:var(--as-loaded-tint)] border-[color:var(--as-loaded-border)]',
    count: 'text-[color:var(--as-loaded-text)]',
    badge: 'bg-[color:var(--as-loaded-solid)] text-[color:var(--as-loaded-on)]',
    bar: 'bg-[color:var(--as-loaded-solid)]',
    text: 'text-[color:var(--as-loaded-text)]',
    pill: 'bg-[color:var(--as-loaded-tint)] text-[color:var(--as-loaded-text)] ring-1 ring-inset ring-[color:var(--as-loaded-border)]',
    dot: 'bg-[color:var(--as-loaded-solid)]',
  },
  at_destination: {
    panel:
      'bg-[color:var(--as-at-destination-tint)] border-[color:var(--as-at-destination-border)]',
    count: 'text-[color:var(--as-at-destination-text)]',
    badge: 'bg-[color:var(--as-at-destination-solid)] text-[color:var(--as-at-destination-on)]',
    bar: 'bg-[color:var(--as-at-destination-solid)]',
    text: 'text-[color:var(--as-at-destination-text)]',
    pill: 'bg-[color:var(--as-at-destination-tint)] text-[color:var(--as-at-destination-text)] ring-1 ring-inset ring-[color:var(--as-at-destination-border)]',
    dot: 'bg-[color:var(--as-at-destination-solid)]',
  },
  empty: {
    panel: 'bg-[color:var(--as-empty-tint)] border-[color:var(--as-empty-border)]',
    count: 'text-[color:var(--as-empty-text)]',
    badge: 'bg-[color:var(--as-empty-solid)] text-[color:var(--as-empty-on)]',
    bar: 'bg-[color:var(--as-empty-solid)]',
    text: 'text-[color:var(--as-empty-text)]',
    pill: 'bg-[color:var(--as-empty-tint)] text-[color:var(--as-empty-text)] ring-1 ring-inset ring-[color:var(--as-empty-border)]',
    dot: 'bg-[color:var(--as-empty-solid)]',
  },
  delay: {
    panel: 'bg-[color:var(--as-delay-tint)] border-[color:var(--as-delay-border)]',
    count: 'text-[color:var(--as-delay-text)]',
    badge: 'bg-[color:var(--as-delay-solid)] text-[color:var(--as-delay-on)]',
    bar: 'bg-[color:var(--as-delay-solid)]',
    text: 'text-[color:var(--as-delay-text)]',
    pill: 'bg-[color:var(--as-delay-tint)] text-[color:var(--as-delay-text)] ring-1 ring-inset ring-[color:var(--as-delay-border)]',
    dot: 'bg-[color:var(--as-delay-solid)]',
  },
  parked: {
    panel: 'bg-[color:var(--as-parked-tint)] border-[color:var(--as-parked-border)]',
    count: 'text-[color:var(--as-parked-text)]',
    badge: 'bg-[color:var(--as-parked-solid)] text-[color:var(--as-parked-on)]',
    bar: 'bg-[color:var(--as-parked-solid)]',
    text: 'text-[color:var(--as-parked-text)]',
    pill: 'bg-[color:var(--as-parked-tint)] text-[color:var(--as-parked-text)] ring-1 ring-inset ring-[color:var(--as-parked-border)]',
    dot: 'bg-[color:var(--as-parked-solid)]',
  },
  maintenance: {
    panel: 'bg-[color:var(--as-maintenance-tint)] border-[color:var(--as-maintenance-border)]',
    count: 'text-[color:var(--as-maintenance-text)]',
    badge: 'bg-[color:var(--as-maintenance-solid)] text-[color:var(--as-maintenance-on)]',
    bar: 'bg-[color:var(--as-maintenance-solid)]',
    text: 'text-[color:var(--as-maintenance-text)]',
    pill: 'bg-[color:var(--as-maintenance-tint)] text-[color:var(--as-maintenance-text)] ring-1 ring-inset ring-[color:var(--as-maintenance-border)]',
    dot: 'bg-[color:var(--as-maintenance-solid)]',
  },
  planned_maintenance: {
    panel:
      'bg-[color:var(--as-planned-maintenance-tint)] border-[color:var(--as-planned-maintenance-border)]',
    count: 'text-[color:var(--as-planned-maintenance-text)]',
    badge:
      'bg-[color:var(--as-planned-maintenance-solid)] text-[color:var(--as-planned-maintenance-on)]',
    bar: 'bg-[color:var(--as-planned-maintenance-solid)]',
    text: 'text-[color:var(--as-planned-maintenance-text)]',
    pill: 'bg-[color:var(--as-planned-maintenance-tint)] text-[color:var(--as-planned-maintenance-text)] ring-1 ring-inset ring-[color:var(--as-planned-maintenance-border)]',
    dot: 'bg-[color:var(--as-planned-maintenance-solid)]',
  },
  breakdown: {
    panel: 'bg-[color:var(--as-breakdown-tint)] border-[color:var(--as-breakdown-border)]',
    count: 'text-[color:var(--as-breakdown-text)]',
    badge: 'bg-[color:var(--as-breakdown-solid)] text-[color:var(--as-breakdown-on)]',
    bar: 'bg-[color:var(--as-breakdown-solid)]',
    text: 'text-[color:var(--as-breakdown-text)]',
    pill: 'bg-[color:var(--as-breakdown-tint)] text-[color:var(--as-breakdown-text)] ring-1 ring-inset ring-[color:var(--as-breakdown-border)]',
    dot: 'bg-[color:var(--as-breakdown-solid)]',
  },
  offline: {
    panel:
      'bg-[color:var(--as-offline-tint)] border-[color:var(--as-offline-border)] border-dashed',
    count: 'text-[color:var(--as-offline-text)]',
    badge:
      'bg-surface text-[color:var(--as-offline-text)] ring-2 ring-inset ring-[color:var(--as-offline-border)]',
    bar: 'bg-[color:var(--as-offline-border)]',
    text: 'text-[color:var(--as-offline-text)]',
    pill: 'bg-transparent text-[color:var(--as-offline-text)] ring-1 ring-inset ring-[color:var(--as-offline-border)]',
    dot: 'bg-transparent ring-1 ring-inset ring-[color:var(--as-offline-solid)]',
  },
  unallocated: {
    panel: 'bg-[color:var(--as-unallocated-tint)] border-[color:var(--as-unallocated-border)]',
    count: 'text-[color:var(--as-unallocated-text)]',
    badge: 'bg-[color:var(--as-unallocated-solid)] text-[color:var(--as-unallocated-on)]',
    bar: 'bg-[color:var(--as-unallocated-solid)]',
    text: 'text-[color:var(--as-unallocated-text)]',
    pill: 'bg-[color:var(--as-unallocated-tint)] text-[color:var(--as-unallocated-text)] ring-1 ring-inset ring-[color:var(--as-unallocated-border)]',
    dot: 'bg-[color:var(--as-unallocated-solid)]',
  },
};

// ── Mapping: one truck → one key (via the rules, map/assetVisualRules) ──────────────────────

/** The key for a trip stage (Replay's track, Stage lane and marker). */
export const statusKeyOfStage = (
  stage: string | null | undefined,
  moving = false
): AssetStatusKey => resolveAssetKey(assetStateOfStage(stage, moving));

/** The key for an asset known only by its motion: moving = unallocated, standing = parked. */
export const statusKeyOfMotion = (
  motion: MotionStatus | string | null | undefined
): AssetStatusKey => resolveAssetKey(assetStateOfMotion(motion));

/** The operational context groups (Status Board tiles / Kanban columns) → key. */
const CONTEXT_GROUP_KEY: Record<string, AssetStatusKey> = {
  to_source: 'to_source',
  at_source: 'at_source',
  loaded: 'loaded',
  // A truck at a weighbridge is at its source or its destination (both green).
  weighbridge: 'at_source',
  at_destination: 'at_destination',
  empty: 'empty',
  delay: 'delay',
  depot: 'parked',
  maintenance: 'maintenance',
  unallocated: 'unallocated',
};
export const statusKeyOfContextGroup = (groupId: string | null | undefined): AssetStatusKey =>
  (groupId && CONTEXT_GROUP_KEY[groupId]) || 'unallocated';

/**
 * The key for a two-axis status alone (no row around it), by the rules. With the whole status
 * board row, use controlRoom's statusKeyOfRow, which also reads the trip stage, maintenance blocks
 * and the data age.
 */
export const statusKeyOfStatus = (status: TwoAxisStatusLike | null | undefined): AssetStatusKey =>
  resolveAssetKey(assetStateOfStatus(status));

// ── The maintenance module's time categories (ST-00249) ──────────────────────────────────────
/** A time-model category (maintenance timeline, legend, counts) → its key; Available has none. */
export type MaintenanceTimeCategory = 'breakdown' | 'planned_maintenance' | 'delay' | 'available';
export const MAINTENANCE_CATEGORY_KEY: Record<MaintenanceTimeCategory, AssetStatusKey | null> = {
  breakdown: 'breakdown',
  planned_maintenance: 'planned_maintenance',
  delay: 'delay',
  available: null,
};
